import React from "react";
import { fetchItemList } from "../items.api";
import { fetchUserProcessLog } from "../../../api/users";
import { apiHost } from "../../../shared/api/helpers";
import type { ApiErrorSetter } from "../../../shared/api/helpers";
import { apiWithTimeout } from "../../../shared/api/timeout";
import {
    getProcessResultDescription,
    isDryRunProcessLog,
    ITEM_PROCESS_HIGHLIGHT_DURATION_MS,
    scrollItemRowIntoView,
} from "../../../shared/lib/helpers";
import { toast } from "../../../shared/ui/toast";
import { ProcessStatusIcon } from "../../process-records/components/ProcessStatusIcon";

export type ItemRowHighlightType = "process" | "focus";

export type ItemRowHighlight = {
    itemId: string;
    type: ItemRowHighlightType;
} | null;

export const PROCESS_LOG_LIMIT = 30;

export const prependProcessLog = (logs: any[], log: any) => (
    [log, ...logs].slice(0, PROCESS_LOG_LIMIT)
);

/** 사이드바 리스트용 안정 키 (WS 메시지는 id가 없을 수 있음) */
export const getProcessLogReactKey = (log: any): string => {
    if (log?.id != null && log.id !== "") {
        return `id-${log.id}`;
    }
    return [
        "ws",
        log?.category ?? "",
        log?.item_id ?? "",
        log?.task_id ?? "",
        log?.started_at ?? "",
        log?.finished_at ?? "",
        log?.message ?? "",
    ].join("|");
};

export const getItemProcessToastId = (message: any): string => (
    `item-process-${message?.item_id ?? "general"}`
);

export interface ItemContextType {
    items: any[];
    processLogs: any[];
    highlightedItem: ItemRowHighlight;
    itemProcessToastEnabled: boolean;
    setItems: (items: any[]) => void;
    updateItem: (itemId: string, updates: Partial<any>) => void;
    setProcessLogs: (logs: any[]) => void;
    setItemProcessToastEnabled: (enabled: boolean) => void;
    addProcessLog: (log: any) => void;
    notifyItemProcess: (message: any) => void;
    highlightItemRow: (itemId: string | number) => void;
    focusItemRow: (itemId: string | number) => boolean;
    fetchItems: (auth: any, setErrorMessage: ApiErrorSetter, params?: any, onResult?: () => void) => Promise<void>;
    fetchProcessLogs: (auth: any, setErrorMessage: ApiErrorSetter) => void;
    fetchSingleItem: (itemId: string, auth: any) => void;
}

export const ItemContext = React.createContext<ItemContextType | null>(null);

export const useItemContext = () => {
    const context = React.useContext(ItemContext);
    if (!context) {
        throw new Error("useItemContext must be used within ItemProvider");
    }
    return context;
};

interface ItemProviderProps {
    children: React.ReactNode;
}

export const ItemProvider: React.FC<ItemProviderProps> = ({ children }) => {
    const [items, setItems] = React.useState<any[]>([]);
    const [processLogs, setProcessLogs] = React.useState<any[]>([]);
    const [highlightedItem, setHighlightedItem] = React.useState<ItemRowHighlight>(null);
    const [itemProcessToastEnabled, setItemProcessToastEnabled] = React.useState<boolean>(true);
    const highlightTimerRef = React.useRef<number | null>(null);

    const setRowHighlight = React.useCallback((itemId: string | number, type: ItemRowHighlightType) => {
        const id = String(itemId);
        if (highlightTimerRef.current !== null) {
            window.clearTimeout(highlightTimerRef.current);
        }
        setHighlightedItem({ itemId: id, type });
        highlightTimerRef.current = window.setTimeout(() => {
            setHighlightedItem((current) => (current?.itemId === id ? null : current));
            highlightTimerRef.current = null;
        }, ITEM_PROCESS_HIGHLIGHT_DURATION_MS);
    }, []);

    const highlightItemRow = React.useCallback((itemId: string | number) => {
        setRowHighlight(itemId, "process");
    }, [setRowHighlight]);

    const focusItemRow = React.useCallback((itemId: string | number): boolean => {
        const found = scrollItemRowIntoView(itemId);
        if (found) {
            setRowHighlight(itemId, "focus");
        }
        return found;
    }, [setRowHighlight]);

    const updateItem = React.useCallback((itemId: string, updates: Partial<any>) => {
        setItems((prevItems) =>
            prevItems.map((item) => {
                // itemId를 string과 number 모두로 비교 (타입 불일치 대응)
                const itemIdStr = String(item.id);
                const updateIdStr = String(itemId);
                return itemIdStr === updateIdStr ? { ...item, ...updates } : item;
            })
        );
    }, []);

    const addProcessLog = React.useCallback((log: any) => {
        setProcessLogs((prevLogs) => prependProcessLog(prevLogs, log));
        if (log.category === "item_process" && log.item_id != null) {
            highlightItemRow(log.item_id);
        }
    }, [highlightItemRow]);

    const notifyItemProcess = React.useCallback((message: any) => {
        if (isDryRunProcessLog(message)) {
            return;
        }
        addProcessLog(message);
        if (!itemProcessToastEnabled) {
            return;
        }

        const toastId = getItemProcessToastId(message);

        if (message.category === "item_process_error") {
            toast.error(message.message || "작업 오류", {
                id: toastId,
                icon: <ProcessStatusIcon isSuccess={false} isDryRun={isDryRunProcessLog(message)} />,
                style: "process",
            });
            return;
        }

        if (message.category !== "item_process") {
            return;
        }

        let itemName = message.item_name || "상품명 없음";
        const shopItemId =
            message.shop_item_id || message.item_shop_id || message.item?.shop_item_id;
        if (shopItemId) {
            itemName += ` (${shopItemId})`;
        }

        if (message.is_success) {
            toast.success(itemName, {
                description: getProcessResultDescription(message),
                id: toastId,
                style: "process",
                icon: <ProcessStatusIcon isSuccess isDryRun={isDryRunProcessLog(message)} />,
            });
        } else {
            toast.error(itemName, {
                description: message.error_message || undefined,
                id: toastId,
                style: "process",
                icon: <ProcessStatusIcon isSuccess={false} isDryRun={isDryRunProcessLog(message)} />,
            });
        }
    }, [addProcessLog, itemProcessToastEnabled]);

    const fetchItemsGenerationRef = React.useRef(0);
    const fetchItems = React.useCallback(
        (auth: any, setErrorMessage: ApiErrorSetter, params?: any, onResult?: () => void) => {
            const generation = ++fetchItemsGenerationRef.current;
            return fetchItemList({
                auth,
                setErrorMessage: (msg, isConnectionFailure) => {
                    if (generation === fetchItemsGenerationRef.current) {
                        setErrorMessage(msg, isConnectionFailure);
                    }
                },
                setResult: (data) => {
                    if (generation === fetchItemsGenerationRef.current) {
                        setItems(data);
                        onResult?.();
                    }
                },
                params: { ...params },
            });
        },
        []
    );

    const fetchSingleItem = React.useCallback(
        (itemId: string, auth: any) => {
            apiWithTimeout.quick.get(`${apiHost}/api/items/${itemId}/`)
                .then((response) => {
                    updateItem(itemId, response.data);
                })
                .catch((error: any) => {
                    if (error.response?.status === 401) {
                        auth?.logout();
                        return;
                    }
                    console.error(`Error fetching item ${itemId}:`, error);
                });
        },
        [updateItem]
    );

    const fetchProcessLogs = React.useCallback(
        (auth: any, setErrorMessage: ApiErrorSetter) => {
            fetchUserProcessLog({
                auth,
                setErrorMessage,
                setResult: setProcessLogs,
            });
        },
        []
    );

    const value = React.useMemo<ItemContextType>(
        () => ({
            items,
            processLogs,
            highlightedItem,
            itemProcessToastEnabled,
            setItems,
            updateItem,
            setProcessLogs,
            setItemProcessToastEnabled,
            addProcessLog,
            notifyItemProcess,
            highlightItemRow,
            focusItemRow,
            fetchItems,
            fetchProcessLogs,
            fetchSingleItem,
        }),
        [items, processLogs, highlightedItem, itemProcessToastEnabled, updateItem, addProcessLog, notifyItemProcess, highlightItemRow, focusItemRow, fetchItems, fetchProcessLogs, fetchSingleItem]
    );

    return (
        <ItemContext.Provider value={value}>{children}</ItemContext.Provider>
    );
};
